import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { setHeaderToken } from '@/common/lib/fetch'
import { shouldUseOauthRedirect } from '@/common/lib/oauthPopup'
import { resolvePostAuthRedirect } from '@/common/lib/safeRedirect'
import { YandexMetrica } from '@/common/lib/yandexMetrica'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import { ROUTES } from '../lib/constants'
import {
    clearOzonAuthPending,
    closeOzonAuthModal,
    getOzonLoginUrl,
    hasNewOzonAuthTokens,
    OZON_AUTH_MESSAGE_SUCCESS,
    OZON_AUTH_POPUP_NAME,
    OZON_AUTH_STORAGE_KEY,
    openOzonAuthModal,
    parseOzonAuthMessage,
    readOzonAuthPending,
    readOzonAuthTokensSnapshot,
    type OzonAuthTokensSnapshot,
} from '../lib/ozonAuth'

const POPUP_WIDTH = 1000
const POPUP_HEIGHT = 700
const POPUP_POLL_INTERVAL_MS = 500
const PENDING_POLL_INTERVAL_MS = 300

const clearAuthState = () => {
    localStorage.removeItem('current_org_id')
}

export const useOzonLogin = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const toast = useOptionalToast()
    const [isLoading, setIsLoading] = useState(false)
    const popupRef = useRef<Window | null>(null)
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const pendingPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const completedRef = useRef(false)
    const tokensSnapshotRef = useRef<OzonAuthTokensSnapshot | null>(null)

    const resetOzonAuthFlow = useCallback(() => {
        completedRef.current = false
        tokensSnapshotRef.current = null
        setIsLoading(false)
        closeOzonAuthModal()
        popupRef.current = null
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current)
            pollTimerRef.current = null
        }
        if (pendingPollTimerRef.current) {
            clearInterval(pendingPollTimerRef.current)
            pendingPollTimerRef.current = null
        }
    }, [])

    const handleAuthSuccess = useCallback(
        (accessToken: string, refreshToken: string) => {
            if (completedRef.current) {
                return
            }
            completedRef.current = true
            clearOzonAuthPending()

            YandexMetrica.reachGoal('auth_success')
            queryClient.clear()
            clearAuthState()
            setHeaderToken(accessToken, refreshToken)
            resetOzonAuthFlow()

            navigate(resolvePostAuthRedirect(searchParams.toString(), ROUTES.APP))
        },
        [navigate, queryClient, resetOzonAuthFlow, searchParams]
    )

    const handleAuthError = useCallback(
        (errorMessage?: string) => {
            if (completedRef.current) {
                return
            }
            completedRef.current = true
            clearOzonAuthPending()
            resetOzonAuthFlow()
            toast?.showToast({
                type: 'error',
                message: errorMessage || 'Не удалось войти через Ozon Seller Id',
            })
        },
        [resetOzonAuthFlow, toast]
    )

    const consumeOzonAuthPending = useCallback((): boolean => {
        const pending = readOzonAuthPending()
        if (!pending) {
            return false
        }

        clearOzonAuthPending()

        if (pending.type === OZON_AUTH_MESSAGE_SUCCESS) {
            handleAuthSuccess(pending.access_token, pending.refresh_token)
            return true
        }

        handleAuthError(pending.error)
        return true
    }, [handleAuthError, handleAuthSuccess])

    const consumeOzonAuthResult = useCallback((): boolean => {
        if (consumeOzonAuthPending()) {
            return true
        }

        const newTokens = hasNewOzonAuthTokens(tokensSnapshotRef.current)
        if (!newTokens) {
            return false
        }

        handleAuthSuccess(newTokens.access_token, newTokens.refresh_token)
        return true
    }, [consumeOzonAuthPending, handleAuthSuccess])

    useEffect(() => {
        consumeOzonAuthPending()
    }, [consumeOzonAuthPending])

    useEffect(() => {
        if (searchParams.get('ozon_auth_error') !== '1') {
            return
        }

        toast?.showToast({
            type: 'error',
            message: 'Не удалось войти через Ozon Seller Id',
        })

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('ozon_auth_error')
        const nextSearch = nextParams.toString()
        navigate(
            {
                pathname: ROUTES.AUTH.LOGIN,
                search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true }
        )
    }, [navigate, searchParams, toast])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const message = parseOzonAuthMessage(event)
            if (!message) {
                return
            }

            if (message.type === OZON_AUTH_MESSAGE_SUCCESS) {
                handleAuthSuccess(message.access_token, message.refresh_token)
                return
            }

            handleAuthError(message.error)
        }

        const handleStorage = (event: StorageEvent) => {
            if (!event.key) {
                return
            }

            if (event.key === OZON_AUTH_STORAGE_KEY && event.newValue) {
                consumeOzonAuthResult()
                return
            }

            if (
                (event.key === 'access_token' || event.key === 'refresh_token') &&
                (isLoading || tokensSnapshotRef.current !== null)
            ) {
                consumeOzonAuthResult()
            }
        }

        window.addEventListener('message', handleMessage)
        window.addEventListener('storage', handleStorage)
        return () => {
            window.removeEventListener('message', handleMessage)
            window.removeEventListener('storage', handleStorage)
        }
    }, [consumeOzonAuthResult, handleAuthError, handleAuthSuccess, isLoading])

    useEffect(() => {
        if (!isLoading) {
            return
        }

        pendingPollTimerRef.current = setInterval(() => {
            consumeOzonAuthResult()
        }, PENDING_POLL_INTERVAL_MS)

        return () => {
            if (pendingPollTimerRef.current) {
                clearInterval(pendingPollTimerRef.current)
                pendingPollTimerRef.current = null
            }
        }
    }, [consumeOzonAuthResult, isLoading])

    useEffect(() => {
        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current)
            }
            if (pendingPollTimerRef.current) {
                clearInterval(pendingPollTimerRef.current)
            }
            popupRef.current?.close()
        }
    }, [])

    const handleOzonLoginClick = useCallback(() => {
        if (isLoading) {
            return
        }

        if (shouldUseOauthRedirect()) {
            tokensSnapshotRef.current = readOzonAuthTokensSnapshot()
            window.location.assign(getOzonLoginUrl())
            return
        }

        completedRef.current = false
        tokensSnapshotRef.current = readOzonAuthTokensSnapshot()
        setIsLoading(true)
        openOzonAuthModal()

        const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2
        const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2
        const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`

        const popup = window.open(getOzonLoginUrl(), OZON_AUTH_POPUP_NAME, features)

        if (!popup) {
            handleAuthError('Разрешите всплывающие окна для входа через Ozon')
            return
        }

        popupRef.current = popup

        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current)
        }

        pollTimerRef.current = setInterval(() => {
            if (!completedRef.current) {
                consumeOzonAuthResult()
            }

            if (!popupRef.current || popupRef.current.closed) {
                if (pollTimerRef.current) {
                    clearInterval(pollTimerRef.current)
                    pollTimerRef.current = null
                }
                popupRef.current = null

                if (!completedRef.current) {
                    if (consumeOzonAuthResult()) {
                        return
                    }
                    resetOzonAuthFlow()
                }
            }
        }, POPUP_POLL_INTERVAL_MS)
    }, [consumeOzonAuthResult, handleAuthError, isLoading, resetOzonAuthFlow])

    const handleCancelOzonLogin = useCallback(() => {
        popupRef.current?.close()
        resetOzonAuthFlow()
    }, [resetOzonAuthFlow])

    return {
        isLoading,
        handleOzonLoginClick,
        handleCancelOzonLogin,
    }
}
